import { Subject } from 'rxjs';
import { Injectable, signal } from '@angular/core';

import { ToastContent } from '../models/toast-content';
import { ToastMessage } from '../models/toast-message.interface';

@Injectable({ providedIn: 'root' })
export class ToasterService {
  private toastSubject = new Subject<ToastMessage>();
  private toastsSignal = signal<ToastMessage[]>([]);
  private counter = 0;
  private timers = new Map<number, ReturnType<typeof setTimeout>>();

  toast$ = this.toastSubject.asObservable();
  toasts = this.toastsSignal.asReadonly();

  private show(toast: Omit<ToastMessage, 'id'>) {
    if (!toast.title && !toast.message) {
      throw new Error('Either title or message must be provided.');
    }

    const newToast: ToastMessage = {
      id: ++this.counter,
      duration: 4000,
      autoHide: true,
      enableHtml: false,
      ...toast,
    };

    // Add to signal for state management
    this.toastsSignal.update((toasts) => [...toasts, newToast]);

    // Emit for legacy observable support
    this.toastSubject.next(newToast);

    // Set up auto-hide timer if enabled
    if (newToast.autoHide && newToast.duration && newToast.duration > 0) {
      const timer = setTimeout(() => {
        this.removeToast(newToast.id);
      }, newToast.duration);
      this.timers.set(newToast.id, timer);
    }
  }

  info(toast: ToastContent & Partial<Pick<ToastMessage, 'duration' | 'autoHide' | 'enableHtml'>>) {
    this.show({ type: 'info', ...toast });
  }

  success(
    toast: ToastContent & Partial<Pick<ToastMessage, 'duration' | 'autoHide' | 'enableHtml'>>,
  ) {
    this.show({ type: 'success', ...toast });
  }

  error(toast: ToastContent & Partial<Pick<ToastMessage, 'duration' | 'autoHide' | 'enableHtml'>>) {
    this.show({ type: 'error', ...toast });
  }

  clearAll(): void {
    // Clear all timers
    this.timers.forEach((timer) => clearTimeout(timer));
    this.timers.clear();
    this.toastsSignal.set([]);
  }

  removeToast(id: number): void {
    // Clear the timer if it exists
    const timer = this.timers.get(id);
    if (timer) {
      clearTimeout(timer);
      this.timers.delete(id);
    }

    this.toastsSignal.update((toasts) => toasts.filter((toast) => toast.id !== id));
  }
}
